/**
 * Queue routing picker for the Omnichannel Queue page — which strategy hands
 * the next ticket to which campaign agent, who counts as routable, and the
 * per-agent open-ticket cap. Saved via PUT /omnichannel-queue/settings; the
 * server immediately re-runs assignment over the waiting items.
 */
import { useEffect, useState, type JSX } from 'react'
import {
  Shuffle,
  ListOrdered,
  RotateCw,
  Inbox,
  CalendarDays,
  Gauge,
  UserCheck,
  Hand,
  Save,
  Settings2,
} from 'lucide-react'
import type { OmniQueueSettings, OmniStrategyInfo, OmniStrategyKey } from './OmnichannelWorkloadPanel.js'

const STRATEGY_ICON: Record<OmniStrategyKey, typeof Shuffle> = {
  fifo: ListOrdered,
  round_robin: RotateCw,
  least_open: Inbox,
  least_today: CalendarDays,
  load_balanced: Gauge,
  sticky: UserCheck,
  random: Shuffle,
  manual: Hand,
}

export default function OmnichannelStrategyPicker({
  settings,
  strategies,
  canManage,
  saving,
  onSave,
}: {
  settings: OmniQueueSettings
  strategies: OmniStrategyInfo[]
  canManage: boolean
  saving: boolean
  onSave: (next: Pick<OmniQueueSettings, 'strategy' | 'agentScope' | 'maxOpenPerAgent'>) => void
}): JSX.Element {
  const [strategy, setStrategy] = useState<OmniStrategyKey>(settings.strategy)
  const [agentScope, setAgentScope] = useState(settings.agentScope)
  const [maxOpen, setMaxOpen] = useState(String(settings.maxOpenPerAgent))

  useEffect(() => {
    setStrategy(settings.strategy)
    setAgentScope(settings.agentScope)
    setMaxOpen(String(settings.maxOpenPerAgent))
  }, [settings.strategy, settings.agentScope, settings.maxOpenPerAgent])

  const maxOpenNum = Math.max(0, Math.min(1000, Math.trunc(Number(maxOpen) || 0)))
  const dirty =
    strategy !== settings.strategy ||
    agentScope !== settings.agentScope ||
    maxOpenNum !== settings.maxOpenPerAgent

  return (
    <div className="rounded-[3px] border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-indigo-600" />
          <h2 className="text-sm font-bold text-gray-900">Queue routing</h2>
          <span className="text-xs text-gray-400">
            Tickets go only to logged-in agents of the ticket’s campaign
          </span>
        </div>
        {canManage ? (
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={() => onSave({ strategy, agentScope, maxOpenPerAgent: maxOpenNum })}
            className="flex items-center gap-1.5 rounded-[3px] bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? 'Saving…' : 'Save routing'}
          </button>
        ) : (
          <span className="text-[11px] text-gray-400">Only supervisors can change routing</span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 xl:grid-cols-4">
        {strategies.map((s) => {
          const Icon = STRATEGY_ICON[s.key] ?? ListOrdered
          const active = strategy === s.key
          return (
            <button
              key={s.key}
              type="button"
              disabled={!canManage}
              onClick={() => setStrategy(s.key)}
              aria-pressed={active}
              className={`flex items-start gap-2.5 rounded-[3px] border p-2.5 text-left transition disabled:cursor-default ${
                active
                  ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200'
                  : 'border-gray-200 bg-white hover:border-indigo-300'
              }`}
            >
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[3px] ${
                  active ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <span className="min-w-0">
                <span className={`block text-xs font-semibold ${active ? 'text-indigo-800' : 'text-gray-800'}`}>
                  {s.label}
                </span>
                <span className="mt-0.5 block text-[11px] leading-snug text-gray-500">{s.description}</span>
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-4 border-t border-gray-100 px-4 py-2.5">
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Routable agents
          <select
            value={agentScope}
            disabled={!canManage}
            onChange={(e) => setAgentScope(e.target.value as OmniQueueSettings['agentScope'])}
            className="rounded-[3px] border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none disabled:bg-gray-50"
          >
            <option value="logged_in">Logged in (not on break)</option>
            <option value="ready">Logged in &amp; Ready only</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Max open tickets per agent
          <input
            type="number"
            min={0}
            max={1000}
            value={maxOpen}
            disabled={!canManage}
            onChange={(e) => setMaxOpen(e.target.value)}
            className="w-20 rounded-[3px] border border-gray-300 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none disabled:bg-gray-50"
          />
          <span className="text-[11px] text-gray-400">0 = no limit</span>
        </label>
      </div>
    </div>
  )
}
